import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckIcon, XIcon, StarIcon, CalendarIcon, PenIcon } from 'lucide-react';
interface User {
  id: string;
  name: string;
  role: string;
  company: string;
  tags: string[];
  goals: string;
  image: string;
}
interface ConnectionScreenProps {
  user: User;
}
export function ConnectionScreen({
  user
}: ConnectionScreenProps) {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  // Mocked connection data - in a real app this would come from an API
  const connection = {
    id: id,
    name: 'Jamie Wilson',
    role: 'Lead Designer',
    company: 'CreativeHub',
    tags: ['UX Design', 'Product Strategy', 'Design Systems', 'Figma'],
    goals: 'Looking for product teams to collaborate with on AI interfaces',
    image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=200&auto=format&fit=crop',
    fitScore: 78,
    sharedTags: ['UX Design', 'Product Strategy'],
    suggestedOpener: 'I see we both work on product strategy and UX design. Have you applied those skills to AI interfaces before?'
  };
  // Calculate which tags are shared between users
  const isSharedTag = (tag: string) => connection.sharedTags.includes(tag);
  return <div className="flex flex-col flex-1" data-id="element-19">
      <header className="p-4 flex items-center border-b" data-id="element-20">
        <button onClick={() => navigate(-1)} className="mr-4" data-id="element-21">
          <XIcon size={24} className="text-gray-600" data-id="element-22" />
        </button>
        <h1 className="text-xl font-bold text-gray-800" data-id="element-23">New Connection</h1>
      </header>
      <div className="flex-1 p-6" data-id="element-24">
        <div className="flex justify-between items-center mb-8" data-id="element-25">
          <div className="flex items-center gap-3" data-id="element-26">
            <img src={connection.image} alt={connection.name} className="w-16 h-16 rounded-full object-cover" data-id="element-27" />
            <div data-id="element-28">
              <h2 className="font-bold text-lg" data-id="element-29">{connection.name}</h2>
              <p className="text-gray-600" data-id="element-30">
                {connection.role} at {connection.company}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-center" data-id="element-31">
            <div className="w-14 h-14 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold text-xl" data-id="element-32">
              {connection.fitScore}
            </div>
            <span className="text-xs text-gray-500 mt-1" data-id="element-33">Fit Score</span>
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg mb-6" data-id="element-34">
          <h3 className="font-semibold text-gray-700 mb-2" data-id="element-35">
            Conversation Starter
          </h3>
          <p className="text-gray-600 italic" data-id="element-36">"{connection.suggestedOpener}"</p>
        </div>
        <div className="mb-6" data-id="element-37">
          <h3 className="font-semibold text-gray-700 mb-2" data-id="element-38">Common Interests</h3>
          <div className="flex flex-wrap gap-2" data-id="element-39">
            {connection.tags.map((tag, index) => <span key={index} className={`${isSharedTag(tag) ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'} text-sm px-3 py-1 rounded-full`} data-id="element-40">
                {tag}{' '}
                {isSharedTag(tag) && <CheckIcon size={14} className="inline ml-1" data-id="element-41" />}
              </span>)}
          </div>
        </div>
        <div className="mb-6" data-id="element-42">
          <h3 className="font-semibold text-gray-700 mb-2" data-id="element-43">Goals</h3>
          <p className="text-gray-600" data-id="element-44">{connection.goals}</p>
        </div>
        <div className="flex flex-col gap-3" data-id="element-45">
          <button className="bg-indigo-600 text-white py-3 rounded-lg flex items-center justify-center gap-2 font-medium" data-id="element-46">
            <CheckIcon size={18} data-id="element-47" />
            Save Connection
          </button>
          <div className="flex gap-3" data-id="element-48">
            <button className="flex-1 bg-white border border-gray-300 py-2 rounded-lg flex items-center justify-center gap-1" data-id="element-49">
              <PenIcon size={16} className="text-gray-600" data-id="element-50" />
              <span className="text-gray-700" data-id="element-51">Add Note</span>
            </button>
            <button className="flex-1 bg-white border border-gray-300 py-2 rounded-lg flex items-center justify-center gap-1" data-id="element-52">
              <CalendarIcon size={16} className="text-gray-600" data-id="element-53" />
              <span className="text-gray-700" data-id="element-54">Schedule</span>
            </button>
            <button className="flex-1 bg-white border border-gray-300 py-2 rounded-lg flex items-center justify-center gap-1" data-id="element-55">
              <StarIcon size={16} className="text-gray-600" data-id="element-56" />
              <span className="text-gray-700" data-id="element-57">Priority</span>
            </button>
          </div>
        </div>
      </div>
    </div>;
}